import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { LeadSourceTagsService } from '../../services/lead-source-tags.service';
import { LeadSourceTag } from '@shared/models/lead-source-tag';
import { LeadSource } from '@shared/types/lead-source';
import { AlertController } from '@ionic/angular';
import { Observable } from 'rxjs';
import { LoadingState } from '@shared/types/loading-state';
import { SessionService } from '@shared/services/session.service';

@Component({
  selector: 'app-lead-source-tags-container',
  templateUrl: './lead-source-tags-container.component.html',
  styleUrls: ['./lead-source-tags-container.component.scss'],
  standalone: false,
})
export class LeadSourceTagsContainerComponent implements OnInit, OnChanges {
  @Input() leadSource: LeadSource;
  @Input() leadSourceTag: string | null;
  @Output() leadSourceTagChange = new EventEmitter<string | null>();
  @ViewChild('leadSourceTagFilter') leadSourceTagFilter: ElementRef<HTMLInputElement>;

  leadSourceTagsLoadingState$: Observable<LoadingState>;
  leadSourceTagsByLeadSource$: Observable<LeadSourceTag[]>;
  filteredLeadSourceTagsByLeadSource$: Observable<LeadSourceTag[]>;
  permissions$ = this.sessionService.getSessionPermissions();

  selectedLeadSourceTag: string | null;
  leadSourceTagFilterTerm = '';
  newLeadSourceTag = '';
  isLeadSourceTagFilterOpen = false;
  filteredLeadSourceTagsThreshold = 5;

  constructor(
    private leadSourceTagsService: LeadSourceTagsService,
    private alertController: AlertController,
    private sessionService: SessionService,
  ) {}

  ngOnInit(): void {
    this.selectedLeadSourceTag = this.leadSourceTag;
    this.leadSourceTagsLoadingState$ = this.leadSourceTagsService.getLeadSourceTagsLoadingState();
    this.leadSourceTagsByLeadSource$ = this.leadSourceTagsService.getLeadSourceTagsByLeadSource(this.leadSource);
    this.filteredLeadSourceTagsByLeadSource$ = this.leadSourceTagsService.getLeadSourceTagsByLeadSource(
      this.leadSource,
      this.leadSourceTagFilterTerm,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadSource']) {
      this.leadSourceTagsByLeadSource$ = this.leadSourceTagsService.getLeadSourceTagsByLeadSource(this.leadSource);
      this.setLeadSourceTagFilterTerm('');
      this.newLeadSourceTag = '';
      this.selectedLeadSourceTag = null;
    }
  }

  toggleLeadSourceTagFilter(): void {
    this.isLeadSourceTagFilterOpen = !this.isLeadSourceTagFilterOpen;
    if (this.isLeadSourceTagFilterOpen) setTimeout(() => this.leadSourceTagFilter.nativeElement.focus());
    if (!this.isLeadSourceTagFilterOpen) this.leadSourceTagFilterTerm = '';
  }

  setLeadSourceTagFilterTerm(filterTerm: string): void {
    this.leadSourceTagFilterTerm = filterTerm.trim();
    this.filteredLeadSourceTagsByLeadSource$ = this.leadSourceTagsService.getLeadSourceTagsByLeadSource(
      this.leadSource,
      this.leadSourceTagFilterTerm,
    );
  }

  selectLeadSourceTag(sourceTag: string): void {
    if (this.newLeadSourceTag.length > 0) this.newLeadSourceTag = '';
    if (this.selectedLeadSourceTag === sourceTag) {
      this.selectedLeadSourceTag = null;
    } else {
      this.selectedLeadSourceTag = sourceTag;
    }
    this.leadSourceTagChange.emit(this.selectedLeadSourceTag);
  }

  async createLeadSourceTag(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Create lead source tag?',
      message: `Create "${this.newLeadSourceTag}" as a new lead source tag?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          id: 'create-lead-source-tag-cancel',
        },
        {
          text: 'Create tag',
          id: 'create-lead-source-tag-confirm',
          role: 'confirm',
          cssClass: 'btn-primary',
          handler: () => {
            this.leadSourceTagsService.createLeadSourceTag({
              attributes: {
                lead_source: this.leadSource,
                name: this.newLeadSourceTag,
              },
            } as Partial<LeadSourceTag>);
            this.selectLeadSourceTag(this.newLeadSourceTag);
          },
        },
      ],
    });

    await alert.present();
  }
}
